import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

export interface DetailPair {
  category?: string;
  focusArea?: string;
}

export interface CriticalElementLike {
  category?: string;
  focusArea?: string;
  details?: DetailPair[];
}

type DetailsInput = CriticalElementLike[] | CriticalElementLike | null | undefined;

export const SKIP_ITA_PAIRS: DetailPair[] = [
  { category: 'Demographic', focusArea: 'Application Type' },
  { category: 'Demographic', focusArea: 'Decision Type' },
  { category: 'Demographic', focusArea: 'Application Month' },
  { category: 'Demographic', focusArea: 'Direct Admit' },
  { category: 'Academic Program', focusArea: 'Academic Interest' },
  { category: 'Academic Program', focusArea: 'Academic Index' },
];

export const HIDE_PAIRS: DetailPair[] = [
  { category: 'Demographic', focusArea: 'Aid Applicant' },
  { category: 'Financial Profile', focusArea: 'Pell' },
];

const normalize = (str: string | undefined | null): string =>
  (str || '').toString().toLowerCase().trim();

const matchesPair = (detail: DetailPair, pair: DetailPair): boolean =>
  normalize(detail.category) === normalize(pair.category) &&
  normalize(detail.focusArea) === normalize(pair.focusArea);

const extractDetails = (input: DetailsInput): DetailPair[] => {
  if (Array.isArray(input)) return input;
  if (input && typeof input === 'object') return input.details || [];

  return [];
};

export const shouldSkipITA = (ceOrDetails: DetailsInput): boolean => {
  const details = extractDetails(ceOrDetails);

  return details.some((d) => SKIP_ITA_PAIRS.some((p) => matchesPair(d, p)));
};

export const shouldHideCE = (ceOrDetails: DetailsInput): boolean => {
  const details = extractDetails(ceOrDetails);

  return details.some((d) => HIDE_PAIRS.some((p) => matchesPair(d, p)));
};

export interface StageDates {
  inquiriesToApps?: string;
  applicationsToAdmits?: string;
  admitsToEnrollment?: string;
}

export type StageKey =
  | 'inquiriesToApps'
  | 'applicationsToAdmits'
  | 'admitsToEnrollment'
  | null;

export const resolveStageKey = (
  { inquiriesToApps, applicationsToAdmits, admitsToEnrollment }: StageDates,
  dateFormat: string,
  now: Dayjs = dayjs(),
): StageKey => {
  const dateITA = dayjs(inquiriesToApps, dateFormat);
  const dateATA = dayjs(applicationsToAdmits, dateFormat);
  const dateATE = dayjs(admitsToEnrollment, dateFormat);

  if (now.isBefore(dateITA)) return null;
  if (now.isBefore(dateATA)) return 'inquiriesToApps';
  if (now.isBefore(dateATE)) return 'applicationsToAdmits';

  return 'admitsToEnrollment';
};

export const areAllCriticalElementsRestricted = (
  criticalElements: CriticalElementLike[] | null | undefined,
): boolean => {
  if (!Array.isArray(criticalElements) || criticalElements.length === 0) {
    return false;
  }

  return criticalElements.every((ce) => shouldHideCE(ce));
};
